/**
 * WRMS Approval Router — Digi Sahyadri
 * Maps department → group → approval chain for each request type
 */

// ── Department → Group Mapping ──────────────────────────────────────────────

const DEPT_GROUP_MAP = {
  // Group B — Operational Departments
  'operations': 'B',
  'finance': 'B',
  'project manager': 'B',
  'business development': 'B',
  'production': 'B',
  
  // Group C — HR Department
  'hr': 'C',
  'human resources': 'C',
  'human resource': 'C',
  'hr department': 'C',
};

/**
 * Get the department group (B, C, D) for a given department name.
 * @param {string} departmentName
 * @param {string} userRole - if role is 'manager', group is D
 * @returns {'B'|'C'|'D'}
 */
export const getDeptGroup = (departmentName, userRole = '') => {
  // Safe case-insensitive check for manager role
  const role = (userRole || '').toLowerCase().trim();
  if (role === 'manager') return 'D';

  const key = (departmentName || '').toLowerCase().trim();
  return DEPT_GROUP_MAP[key] || 'A'; // default to old workflow (A)
};

// ── Approval Chain Definitions ──────────────────────────────────────────────

/**
 * Returns the full ordered approval chain for a given request type & group.
 * Standard flow for all requests:
 * Step 1: Submitted (Employee)
 * Step 2: Handover Colleague Approval (if assigned)
 * Step 3: Reporting Manager Approval (Designated Reporting Manager)
 * Step 4: HR Final Approval (HRM)
 * Each step: { stage: string, role: string, label: string }
 */
export const getApprovalChain = (requestType, deptGroup, options = {}) => {
  const hasHandover = typeof options === 'boolean'
    ? options
    : typeof options === 'string'
    ? Boolean(options.trim())
    : Boolean(options?.handoverTo || options?.hasHandover);

  const chain = [
    { stage: 'submitted', role: 'employee', label: 'Submitted' },
  ];

  if (hasHandover) {
    chain.push({ stage: 'handover', role: 'handover_employee', label: 'Handover Colleague Approval' });
  }

  chain.push(
    { stage: 'manager', role: 'manager', label: 'Reporting Manager Approval' },
    { stage: 'hr', role: 'hr', label: 'HR Final Approval' }
  );

  return chain;
};

/**
 * Normalizes an approval chain to ensure no legacy stages (production_head, dept_head) exist,
 * and ensures that the chain follows the standardized structure.
 */
export const normalizeApprovalChain = (request) => {
  if (!request) return [];
  const rawChain = request.approvalChain;
  const hasHandover = Boolean(request.handoverTo);

  const hasLegacyRole = Array.isArray(rawChain) && rawChain.some(
    s => s.role === 'production_head' || s.stage === 'production_head' || s.role === 'dept_head' || s.stage === 'dept_head'
  );

  if (!rawChain || !Array.isArray(rawChain) || rawChain.length === 0 || hasLegacyRole) {
    return getApprovalChain(request.requestType, request.deptGroup, { hasHandover, handoverTo: request.handoverTo });
  }

  return rawChain;
};

/**
 * Check if a given user is the designated Handover Colleague for a specific request.
 */
export const isHandoverColleagueOfRequest = (approverUser, request) => {
  if (!approverUser || !request) return false;

  const targetId = String(request.handoverToId || '');
  const targetEmail = (request.handoverToEmail || '').toLowerCase().trim();
  const targetName = (request.handoverTo || '').toLowerCase().trim();

  if (!targetId && !targetEmail && !targetName) return false;

  // 1. Check by ID (uid, employeeId, doc ID, employeeCode)
  if (targetId) {
    const userIds = [
      approverUser.id,
      approverUser.uid,
      approverUser.employeeId,
      approverUser.job?.employeeId,
      approverUser.job?.employeeCode,
      approverUser.employeeCode
    ].filter(Boolean).map(String);

    if (userIds.includes(targetId)) return true;
  }

  // 2. Check by Email
  if (targetEmail) {
    const userEmails = [
      approverUser.email,
      approverUser.personal?.email,
      approverUser.contact?.email
    ].filter(Boolean).map(e => e.toLowerCase().trim());

    if (userEmails.includes(targetEmail)) return true;
  }

  // 3. Check by Name
  if (targetName) {
    const userNames = [
      approverUser.employeeName,
      approverUser.displayName,
      approverUser.personal?.fullName,
      approverUser.name,
      `${approverUser.firstName || ''} ${approverUser.lastName || ''}`.trim()
    ].filter(Boolean).map(s => s.toLowerCase().trim());

    if (userNames.some(name => name && (name === targetName || targetName.includes(name) || name.includes(targetName)))) {
      return true;
    }
  }

  return false;
};

/**
 * Check if a given user is the designated Reporting Manager for a specific request.
 */
export const isReportingManagerOfRequest = (approverUser, request) => {
  if (!approverUser || !request) return false;

  const reqMgrId = String(request.reportingManagerId || '');
  const reqMgrName = (request.reportingManagerName || '').toLowerCase().trim();

  const userIds = [
    approverUser.id,
    approverUser.uid,
    approverUser.employeeId,
    approverUser.job?.employeeId,
    approverUser.job?.employeeCode,
    approverUser.employeeCode
  ].filter(Boolean).map(String);

  // 1. Match by ID
  if (reqMgrId && userIds.includes(reqMgrId)) {
    return true;
  }

  // 2. Match by Email
  const reqMgrEmail = (request.reportingManagerEmail || '').toLowerCase().trim();
  if (reqMgrEmail) {
    const userEmails = [
      approverUser.email,
      approverUser.personal?.email,
      approverUser.contact?.email
    ].filter(Boolean).map(e => e.toLowerCase().trim());

    if (userEmails.includes(reqMgrEmail)) return true;
  }

  // 3. Match by Name
  if (reqMgrName) {
    const userNames = [
      approverUser.employeeName,
      approverUser.displayName,
      approverUser.personal?.fullName,
      approverUser.name,
      `${approverUser.firstName || ''} ${approverUser.lastName || ''}`.trim()
    ].filter(Boolean).map(s => s.toLowerCase().trim());

    if (userNames.some(name => name && (name === reqMgrName || reqMgrName.includes(name) || name.includes(reqMgrName)))) {
      return true;
    }
  }

  // 4. Fallback: match via employee's job reportingManager reference if not directly on request
  if (!reqMgrId && !reqMgrName) {
    const empMgrId = String(request.employeeJob?.reportingManager || request.job?.reportingManager || '');
    if (empMgrId && userIds.includes(empMgrId)) {
      return true;
    }
  }

  return false;
};

/**
 * Get the next stage after the current stage in the approval chain.
 * Returns null if current stage is the last one (final approval reached).
 * @param {string} requestType
 * @param {string} deptGroup
 * @param {string} currentStage
 * @param {object} request
 * @returns {{ stage: string, role: string, label: string } | null}
 */
export const getNextStage = (requestType, deptGroup, currentStage, request = null) => {
  const chain = normalizeApprovalChain(request) || getApprovalChain(requestType, deptGroup, Boolean(request?.handoverTo));
  
  // If legacy currentStage was dept_head or production_head, map index to manager
  let normalizedStage = currentStage;
  if (currentStage === 'dept_head' || currentStage === 'production_head') {
    normalizedStage = 'manager';
  }

  const currentIndex = chain.findIndex(s => s.stage === normalizedStage);
  if (currentIndex === -1 || currentIndex >= chain.length - 1) return null;
  return chain[currentIndex + 1];
};

/**
 * Check if the given role is currently an approver for this request.
 * Strictly verifies designated reporting manager for manager stage.
 * @param {string} requestType
 * @param {string} deptGroup
 * @param {string} currentStage - the stage the request is currently at
 * @param {string} userRole
 * @param {string} userDept
 * @param {string} requestDept
 * @param {object} user
 * @param {object} request
 * @returns {boolean}
 */
export const isCurrentApprover = (requestType, deptGroup, currentStage, userRole, userDept = null, requestDept = null, user = null, request = null) => {
  if (!currentStage) return false;

  const role = (userRole || user?.role || '').toLowerCase().trim();

  // Management roles (admin, hr, ceo, founder) can always approve or override
  if (['admin', 'hr', 'ceo', 'founder'].includes(role)) {
    return true;
  }

  // If current stage is 'handover' (Handover Colleague Approval)
  if (currentStage === 'handover') {
    if (request && user) {
      return isHandoverColleagueOfRequest(user, request);
    }
    return false;
  }

  // If current stage is 'manager' (or legacy dept_head/production_head awaiting manager)
  if (currentStage === 'manager' || currentStage === 'dept_head' || currentStage === 'production_head') {
    if (request && user) {
      return isReportingManagerOfRequest(user, request);
    }
    return role === 'manager';
  }

  // If current stage is 'hr'
  if (currentStage === 'hr') {
    return role === 'hr' || role === 'admin';
  }

  return false;
};

/**
 * Check if this is the final approval stage.
 */
export const isFinalStage = (requestType, deptGroup, currentStage, request = null) => {
  const chain = normalizeApprovalChain(request) || getApprovalChain(requestType, deptGroup, Boolean(request?.handoverTo));
  const last = chain[chain.length - 1];
  return last?.stage === currentStage;
};

/**
 * Get the label for the current stage.
 */
export const getStageLabelForRole = (role) => {
  const labels = {
    employee: 'Employee',
    handover_employee: 'Handover Colleague',
    handover: 'Handover Colleague',
    manager: 'Reporting Manager',
    hr_head: 'HR Head',
    hr: 'HRM',
    admin: 'Admin',
    ceo: 'CEO',
    founder: 'Founder',
    director: 'Director',
    dept_head: 'Reporting Manager',
    production_head: 'Reporting Manager',
  };
  return labels[role] || role;
};
